import { ChangeEvent, KeyboardEvent, useEffect, useRef, useState } from "react";
import { Dropdown, DropdownItem, DropdownMenu, DropdownToggle, Input, InputProps } from "reactstrap";
import useToggle from "../../../hook/useToggle";
import { IOption } from "../../../types/common";
import './style.scss';

export interface SearchInputProps extends InputProps {
  auto?: boolean;
  options?: IOption[];
  onSearch: (str: string) => void;
  onConfirm: (option: IOption) => void; // user selected an item from the search results
}

const SearchInput = (props: SearchInputProps) => {
  const { auto, options, onSearch, onConfirm: onUpdate, onChange, onKeyDown, ...inputProps } = props;

  const { isOpen, toggle } = useToggle();

  const setIsOpen = (open: boolean) => open !== isOpen && toggle();

  const [busy, setBusy] = useState(false);

  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (busy && options) {
      setBusy(false);
      setIsOpen(true);

      if (options.length === 0) {
        inputRef.current?.focus();
      }
    }
  }, [options, busy]);

  const search = (str: string) => {
    if (str) {
      setBusy(true);
      onSearch(str);
    }
  };

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    const { value } = event.target;
    if (!value) {
      setIsOpen(false);
    } else if (auto) {
      search(value);
    }

    onChange?.(event);
  };

  const handleSearch = () => {
    if (inputRef.current) {
      search(inputRef.current?.value);
    }
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter' && inputRef.current?.value && !auto) {
      handleSearch();
    }

    onKeyDown?.(event);
  };

  const handleSelect = (option: IOption) => {
    if (inputRef.current) {
      inputRef.current.value = option.label;
    }

    onUpdate(option);

    setTimeout(() => {
      setIsOpen(false);
      inputRef.current?.focus();
    }, 100);
  };

  const handleSearchFieldClick = () => {
    if (!isOpen) 
      handleSearch()
  };

  return (
    <Dropdown isOpen={isOpen && !busy} toggle={toggle} direction="down">
      <DropdownToggle tag="span" className={inputProps.invalid ? 'is-invalid' : ''}>
        <Input {...inputProps} type="text"
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          onClick={handleSearchFieldClick}
          autoComplete={auto && inputRef.current?.value ? 'off' : inputProps.autoComplete}
          innerRef={inputRef} />
      </DropdownToggle>

      {(inputRef.current?.value || options) && 
      <DropdownMenu>
          {!busy && options?.length === 0 && (
            <DropdownItem disabled>
              <i>No results found.</i>
            </DropdownItem>
          )}
          {options?.map((o) => (
            <DropdownItem key={o.id} onClick={() => handleSelect(o)} disabled={!o.id}>
              {o.displayItem ?? o.label}
            </DropdownItem>
          ))}
        </DropdownMenu>
      }
    </Dropdown>
  );
};

export default SearchInput;
